import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import Skeleton from 'react-loading-skeleton';

import Button from '~/shared/components/ButtonsV2';
import { Input } from '~/shared/components/Input';
import { useXFetch } from '~/shared/utils/useXFetch';
import Loader from '~/shared/components/Loader';
import Typography from '~/shared/components/Typography';
import Tag from '~/shared/components/Tag';
import { toaster } from '~/shared/utils/toaster';

import { SectionFooter } from './SectionFooter';

type RoleArnInfo = { roleArn: string };

export function RoleArn({ activeTenantName }: { activeTenantName: string }) {
  const [forceEditable, setForceEditable] = useState(false);
  const search = useLocation().search;
  const showDevTools = !!new URLSearchParams(search).get('devtools');
  const api = useXFetch();

  const {
    data: roleArn,
    isLoading: isQueryLoading,
    refetch,
  } = useQuery({
    queryKey: ['getRoleArn'],
    queryFn: async () => {
      const { roleArn } = await api<RoleArnInfo>('/api/v1/accounts/role-arn');
      reset({ roleArn });
      return roleArn;
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    reset,
    getValues,
    // setValue,
    formState: { isLoading, isSubmitting, errors, isValid, isDirty },
    control,
  } = useForm<RoleArnInfo>();

  const updateRoleArnMutation = useMutation({
    mutationFn: async (values: RoleArnInfo) => {
      return await api<RoleArnInfo>('/api/v1/accounts/role-arn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roleArn: values.roleArn,
        }),
      });
    },
    onSuccess: () => {
      toaster({
        title: 'IAM Role Updated',
        message: 'The IAM role was updated successfully',
        variant: 'success',
      });
      refetch();
      setForceEditable(false);
    },
    onError: (error: Error) => {
      let title = 'Invalid ARN';
      let message = error.message;

      if (error.message === 'Internal server error') {
        message = 'Input is not a valid IAM role ARN';
      } else if (error.message === 'Your ARN does not match a role with the required permissions') {
        title = 'Invalid IAM Role';
        message =
          'The IAM role defined by the ARN does not have the required permissions, or could not be validated.';
      }

      toaster({
        title,
        message,
        variant: 'error',
      });
      setError('root', { message: error.message });
    },
  });

  const onSubmit: SubmitHandler<RoleArnInfo> = (values) => {
    updateRoleArnMutation.mutate(values);
  };

  const showEditButton =
    !forceEditable && roleArn && !errors?.root && !updateRoleArnMutation.isPending;
  const showApplyButton = !showEditButton;
  const isInputDisabled =
    (!forceEditable && roleArn && !errors?.root) || isLoading || isQueryLoading;

  return (
    <div className="px-[14px]">
      <div className="flex items-center gap-2 py-[30px]">
        <Typography variant="H4-med" fontWeight={500} className="text-primary-900">
          Role&apos;s ARN
        </Typography>
        {activeTenantName ? (
          <Tag
            text={activeTenantName || ''}
            variant="default"
            className="capitalize text-gray-700!"
          />
        ) : (
          <Skeleton
            containerClassName=" block leading-[unset!important]"
            className="h-[28px] leading-[unset!important] rounded-[8px] "
            baseColor="var(--color-gray-200)"
            highlightColor="#FFFF"
            width={80}
          />
        )}
      </div>
      {showDevTools && <DevTool control={control} />}

      <div className="">
        <Typography variant="p3" fontWeight={600} className="text-primary-900 px-0.5 py-1">
          Datafy IAM Role ARN
        </Typography>
        <div className="flex gap-[10px]">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 gap-[10px]">
            <Input
              {...register('roleArn', { required: true })}
              disabled={isInputDisabled}
              placeholder="Paste here"
              wrapperClassName="w-full"
            />
            {showApplyButton && (
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={
                    !isValid ||
                    (!isDirty && roleArn && !forceEditable && !errors?.root) ||
                    isLoading ||
                    isQueryLoading ||
                    updateRoleArnMutation.isPending
                  }
                  className="w-[80px] disabled:opacity-30"
                >
                  {updateRoleArnMutation.isPending ? (
                    <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Apply'
                  )}
                </Button>
              </div>
            )}
          </form>
          {showEditButton && (
            <Button
              type="button"
              variant="third"
              disabled={isLoading || isQueryLoading}
              onClick={() => {
                setForceEditable(true);
                reset(getValues(), {});
              }}
              className="w-[80px] disabled:opacity-30"
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      <SectionFooter
        infoProps={
          isLoading || isQueryLoading || isSubmitting || updateRoleArnMutation.isPending
            ? {
                variant: 'none',
                text: (
                  <div className="h-[24px] py-1 flex items-center">
                    <Skeleton
                      containerClassName=" block leading-[unset!important]"
                      className="h-[12px] leading-[unset!important] rounded-[8px] "
                      baseColor="var(--color-gray-200)"
                      highlightColor="#FFFF"
                      width={200}
                    />
                  </div>
                ),
              }
            : errors.root
              ? {
                  text: 'Input is invalid',
                  // text: 'Your ARN code was incorrect. Please copy the role ARN which has the trust relationship',
                  variant: 'error',
                }
              : forceEditable || roleArn
                ? {
                    text: (
                      <span>Input the ARN of the IAM role that provides Datafy permissions </span>
                    ),
                    variant: 'success',
                  }
                : { text: 'Input the ARN of the IAM role that provides Datafy permissions' }
        }
        buttonEl={
          <Button
            type="submit"
            variant="primary"
            disabled={
              !isValid ||
              (!isDirty && roleArn && !forceEditable && !errors?.root) ||
              isLoading ||
              isQueryLoading ||
              updateRoleArnMutation.isPending
            }
            className="w-[121px] gap-[10px] disabled:opacity-30"
          >
            {updateRoleArnMutation.isPending ? (
              <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              'Confirm'
            )}
          </Button>
        }
      />
    </div>
  );
}
